import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TrackByFunction } from '@angular/core';

import { remove } from 'lodash-es';

import { SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

import { filter, Observable, Subject, takeUntil } from 'rxjs';

import { IPaperAuthorCreateDialogData } from 'editing-tool/src/app/shared/components/paper/steps/authors/author-create-dialog/paper-author-create-dialog-data';
import { PaperAuthorCreateDialogComponent } from 'editing-tool/src/app/shared/components/paper/steps/authors/author-create-dialog/paper-author-create-dialog.component';
import { IPaperAuthorSearchDialogData } from 'editing-tool/src/app/shared/components/paper/steps/authors/author-search-dialog/paper-author-search-dialog-data';
import { PaperAuthorSearchDialogComponent } from 'editing-tool/src/app/shared/components/paper/steps/authors/author-search-dialog/paper-author-search-dialog.component';
import { IPaperAuthorsData } from 'editing-tool/src/app/shared/components/paper/steps/authors/paper-authors-data';
import { PaperAuthorsRenderers } from 'editing-tool/src/app/shared/components/paper/steps/authors/renderers/paper-authors-renderers';
import { CommonSelectItemsService, ScConfirmationService, TableSettings } from 'sc-common';
import { ApiEditingToolPaperAuthorListItem, ApiEditingToolCreateAuthorRequest, ApiEditingToolParsedAuthor, ApiEditingToolExistingParsedAuthor } from 'sc-common/core/services/open-api/main-api-client';

@Component({
    selector: 'et-paper-authors',
    templateUrl: './paper-authors.component.html',
    standalone: false
})
export class PaperAuthorsComponent implements OnInit, OnDestroy {

    @Input()
    public data: IPaperAuthorsData;

    @Input()
    public isReadonly: boolean;

    @Output()
    public readonly authorsCountChanged = new EventEmitter();

    public existingAuthors: ApiEditingToolParsedAuthor[] = [];

    public newAuthors: ApiEditingToolParsedAuthor[] = [];

    public readonly trackByFunc: TrackByFunction<ApiEditingToolParsedAuthor> = (_: number, item: ApiEditingToolParsedAuthor): any => item.memberId || item.email;

    public readonly countries$: Observable<SelectItem[]>;

    public readonly titles$: Observable<SelectItem[]>;

    public tableSettings: TableSettings<ApiEditingToolPaperAuthorListItem>;

    private readonly _destroy$ = new Subject<void>();

    private readonly _refreshTable$ = new Subject<void>();

    constructor(
        private readonly _dialogService: DialogService,
        private readonly _confirmationService: ScConfirmationService,
        selectItemsService: CommonSelectItemsService) {

        this.countries$ = selectItemsService.countries$;
        this.titles$ = selectItemsService.memberTitles$;
    }

    public ngOnInit(): void {

        if (!this.isReadonly) {
            this._getData();
        } else {
            this.existingAuthors = [];
            this.newAuthors = [];
        }

        this._initTable();
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
    }

    public onAuthorRemoved(memberId: number): void {

        this._confirmationService.confirm({
            title: $localize`Remove Author`,
            message: $localize`Are you sure to remove this author?`,
            acceptLabel: $localize`Yes`,
            rejectLabel: $localize`No`,
            accept$: this.data.removeAuthor(memberId),
            acceptCallback: () => {
                this._refreshTable$.next();
                this.authorsCountChanged.emit();
            }
        });
    }

    public onParsedAuthorAdded(apiModel: ApiEditingToolParsedAuthor, isNewAuthor: boolean): void {
        const request$ = isNewAuthor
            ? this.data.addNewParsedAuthor(apiModel)
            : this.data.addExistingParsedAuthor(
                new ApiEditingToolExistingParsedAuthor({ memberId: apiModel.memberId, parsedAuthorId: apiModel.parsedAuthorId }));

        request$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this._removeParsedAuthorFromArray(apiModel.memberId, isNewAuthor);
                this.authorsCountChanged.emit();
                this._refreshTable$.next();
            });
    }

    public onParsedAuthorRemoved(apiModel: ApiEditingToolParsedAuthor, isNewAuthor: boolean): void {

        this._confirmationService.confirm({
            title: $localize`Remove Author`,
            message: $localize`Are you sure?`,
            acceptLabel: $localize`Yes`,
            rejectLabel: $localize`No`,
            accept$: this.data.deleteParsedAuthor(apiModel.parsedAuthorId),
            acceptCallback: () => {
                this._removeParsedAuthorFromArray(apiModel.memberId, isNewAuthor);
            }
        });
    }

    public createAuthor(): void {
        const dialogRef = this._dialogService.open(PaperAuthorCreateDialogComponent, {
            width: '35vw',
            header: $localize`Create Author`,
            closable: true,
            data: {
                create: (apiModel: ApiEditingToolCreateAuthorRequest) => this.data.createAuthor(apiModel),
                isOrcidIdUnusedRequest: (orcidId: string) => this.data.isMemberOrcidIdUnused(orcidId)
            } as IPaperAuthorCreateDialogData
        });

        dialogRef.onClose
            .pipe(
                filter(result => !!result),
                takeUntil(this._destroy$))
            .subscribe(() => {
                this.authorsCountChanged.emit();
                this._refreshTable$.next();
            });
    }

    public searchAuthors(): void {
        const dialogRef = this._dialogService.open(PaperAuthorSearchDialogComponent, {
            width: '39vw',
            header: $localize`Search authors`,
            closable: true,
            data: {
                findAuthorsRequest: (query: string) => this.data.findAuthors(query),
                addAuthorsRequest: (memberIdArray: number[]) => this.data.addExistingAuthors(memberIdArray)
            } as IPaperAuthorSearchDialogData
        });

        dialogRef.onClose
            .pipe(
                filter(i => !!i),
                takeUntil(this._destroy$))
            .subscribe(() => {
                this.authorsCountChanged.emit();
                this._refreshTable$.next();
            });
    }

    private _initTable(): void {

        this.tableSettings = TableSettings.create(ApiEditingToolPaperAuthorListItem, {
            dataSource: oDataQuery => this.data.getList(oDataQuery),
            columns: {
                pos: {
                    width: '60px',
                    centered: true,
                    sortable: false,
                    filterable: false
                },
                firstName: {
                    renderer: PaperAuthorsRenderers.firstName(),
                    sortable: false,
                    filterable: false
                },
                lastName: {
                    sortable: false,
                    filterable: false
                },
                email: {
                    sortable: false,
                    filterable: false
                },
                affiliation: {
                    renderer: PaperAuthorsRenderers.affiliation(this.data.getAffiliationAction, this.countries$),
                    width: '120px',
                    centered: true,
                    sortable: false,
                    filterable: false
                }
            },
            defaultSort: {
                field: x => x.pos,
                order: 'asc'
            },
            reordering: this.isReadonly ?
                null :
                {
                    field: x => x.pos,
                    acceptCallback: order => this.data.updateOrder(order),
                    separateColumn: true
                },
            reload$: this._refreshTable$.asObservable(),
            actionsCount: 1,
            columnResizeMode: 'fit',
            paginator: false,
            rowHover: false,
            hasFilters: false
        });
    }

    private _removeParsedAuthorFromArray(memberId: number, isNewAuthor: boolean): void {
        const authorsToUpdateArray = isNewAuthor ? this.newAuthors : this.existingAuthors;

        remove(authorsToUpdateArray, author => author.memberId === memberId);
        this._saveToStorage();
        this.authorsCountChanged.emit();
    }

    private _saveToStorage(): void {
        const data = {
            existingAuthors: this.existingAuthors,
            newAuthors: this.newAuthors
        };

        this.data.saveParsedAuthors(data);
    }

    private _getData(): void {

        this.data.getParsedAuthors()
            .pipe(takeUntil(this._destroy$))
            .subscribe(parsedAuthors => {
                this.existingAuthors = parsedAuthors.existingAuthors;
                this.newAuthors = parsedAuthors.newAuthors;
            });
    }
}
